import { EProductsInsurance, ETax } from '@if/core/types'
import { InvoiceTable, type InvoiceRow } from '@if/mui'
import { FederalTaxIds } from '@src/types/Constants'
import { ProductDetails, ProductsFundingDetails } from '@src/types/FundingSummaryDto'
import { useTranslation } from 'react-i18next'

interface Props {
  productsFundingDetails: ProductsFundingDetails
}

interface ProductRow {
  labelKey: EProductsInsurance
  details: ProductDetails
}

const sumTaxesByIds = (rows: ProductRow[], taxIds: ETax[]) =>
  rows.reduce(
    (sum, r) =>
      sum + r.details.retentionTaxes.filter((t) => taxIds.includes(t.taxId as ETax)).reduce((s, t) => s + t.amount, 0),
    0,
  )

const RetentionBreakdown = ({ productsFundingDetails }: Props) => {
  const { t } = useTranslation()

  const productRows: ProductRow[] = (
    [
      { labelKey: EProductsInsurance.ExtendedWarranty, details: productsFundingDetails.warrantyDetails },
      { labelKey: EProductsInsurance.CreditInsurance, details: productsFundingDetails.creditInsuranceDetails },
      {
        labelKey: EProductsInsurance.ReplacementOrGapInsurance,
        details: productsFundingDetails.replacementInsuranceDetails,
      },
    ] as const
  ).filter((r): r is ProductRow => r.details !== null)

  const productRetentionSubtotal = productRows.reduce((s, r) => s + r.details.retentionAmount, 0)
  const taxFederalAmount = sumTaxesByIds(productRows, FederalTaxIds)
  const taxQuebecAmount = sumTaxesByIds(productRows, [ETax.QST])
  const retentionTotal = productRetentionSubtotal + taxFederalAmount + taxQuebecAmount
  const hasAnyTax = taxFederalAmount > 0 || taxQuebecAmount > 0
  const amountPaidOut = productsFundingDetails.totalPayoutAmount
  const totalBeforeRetention = amountPaidOut + retentionTotal

  const rows: InvoiceRow[] = [
    { label: t('fundingSummary.totalBeforeRetention'), value: totalBeforeRetention },
    ...(hasAnyTax ? [{ label: t('fundingSummary.retentionSubtotal'), value: -productRetentionSubtotal }] : []),
    ...(taxFederalAmount > 0 ? [{ label: t('fundingSummary.taxFederal'), value: -taxFederalAmount }] : []),
    ...(taxQuebecAmount > 0 ? [{ label: t('fundingSummary.taxQuebec'), value: -taxQuebecAmount }] : []),
    { label: t('fundingSummary.retentionTotal'), value: -retentionTotal, emphasis: 'subtotal' },
    { label: t('fundingSummary.amountPaidOut'), value: amountPaidOut, emphasis: 'total' },
  ]

  return (
    <InvoiceTable
      columns={
        [
          { label: t('fundingSummary.retentions') },
          { label: t('fundingSummary.priceCol'), type: 'currency' },
          { label: t('fundingSummary.rateCol'), type: 'percent' },
          { label: t('fundingSummary.retentionCol'), type: 'currency' },
        ] as const
      }
      items={productRows.map(({ labelKey, details }) => ({
        cells: [
          t(`fundingSummary.${labelKey}`),
          details.salePrice,
          details.retentionRate,
          -details.retentionAmount,
        ] as [string, number, number, number],
      }))}
      rows={rows}
    />
  )
}

export default RetentionBreakdown
